import { useCallback, useEffect, useState } from "react";
import {
  loadServiceTechManifestWithCache,
  stampServiceTechCheckInLocal,
} from "@/lib/service/serviceTechSync";
import type { ServiceCheckInMilestone, ServiceTechManifest } from "@/lib/service/types";

export function useServiceTechApp() {
  const [manifest, setManifest] = useState<ServiceTechManifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stampingEventId, setStampingEventId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [fromCache, setFromCache] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setSyncing(true);
    setError(null);
    const result = await loadServiceTechManifestWithCache({ horizon_days: 1 });
    setManifest(result.manifest);
    setFromCache(result.fromCache);
    if (result.error) setError(result.error);
    if (result.conflicts.length) {
      setToast("Some check-ins were updated from the server. Review your jobs.");
    }
    setSyncing(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onOnline = () => {
      setOffline(false);
      void refresh();
    };
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [refresh]);

  const checkIn = useCallback(
    async (eventId: string, milestone: ServiceCheckInMilestone) => {
      if (!manifest) return;
      setStampingEventId(eventId);
      setToast(null);
      const result = await stampServiceTechCheckInLocal({
        manifest,
        eventId,
        milestone,
      });
      setManifest(result.manifest);
      if (result.error) setError(result.error);
      if (result.conflicts.length) {
        setToast("Server has newer data for that job. Check in again if needed.");
      } else if (!result.error) {
        setToast("Check-in recorded.");
      }
      setStampingEventId(null);
    },
    [manifest]
  );

  return {
    manifest,
    loading,
    syncing,
    stampingEventId,
    error,
    offline,
    fromCache,
    toast,
    refresh,
    checkIn,
    clearToast: () => setToast(null),
  };
}
